import React from "react";
import { Card } from "primereact/card";
import { Chart } from "primereact/chart";
import { MonthlyAggregate } from "../../../../types/analytics";
import { CHART_COLORS, CHART_FONT, sharedChartOptions } from "./chartConfig";

interface SessionsEnergyChartProps {
  monthly: MonthlyAggregate[];
  hasDetailData: boolean;
}

const SessionsEnergyChart: React.FC<SessionsEnergyChartProps> = ({
  monthly,
  hasDetailData,
}) => {
  const datasets: any[] = hasDetailData
    ? [
        {
          type: "bar" as const,
          label: "Paid Sessions",
          data: monthly.map((m) => m.paidSessions),
          backgroundColor: CHART_COLORS.lime,
          borderWidth: 0,
          borderRadius: { topLeft: 4, topRight: 4, bottomLeft: 0, bottomRight: 0 },
          yAxisID: "y",
          order: 2,
          stack: "sessions",
        },
        {
          type: "bar" as const,
          label: "Free Sessions",
          data: monthly.map((m) => m.freeSessions),
          backgroundColor: CHART_COLORS.primaryLight,
          borderColor: CHART_COLORS.primary,
          borderWidth: 1,
          borderRadius: 0,
          yAxisID: "y",
          order: 2,
          stack: "sessions",
        },
        {
          type: "line" as const,
          label: "Energy (kWh)",
          data: monthly.map((m) => m.energy),
          borderColor: CHART_COLORS.orange,
          backgroundColor: CHART_COLORS.orangeLight,
          fill: false,
          tension: 0.3,
          pointBackgroundColor: CHART_COLORS.orange,
          pointRadius: 4,
          pointHoverRadius: 6,
          yAxisID: "y1",
          order: 1,
        },
      ]
    : [
        {
          type: "bar" as const,
          label: "Sessions",
          data: monthly.map((m) => m.sessions),
          backgroundColor: CHART_COLORS.primary,
          borderWidth: 0,
          borderRadius: 4,
          yAxisID: "y",
          order: 2,
        },
        {
          type: "line" as const,
          label: "Energy (kWh)",
          data: monthly.map((m) => m.energy),
          borderColor: CHART_COLORS.orange,
          backgroundColor: CHART_COLORS.orangeLight,
          fill: false,
          tension: 0.3,
          pointBackgroundColor: CHART_COLORS.orange,
          pointRadius: 4,
          pointHoverRadius: 6,
          yAxisID: "y1",
          order: 1,
        },
      ];

  const chartData = {
    labels: monthly.map((m) => m.label),
    datasets,
  };

  const options = {
    ...sharedChartOptions,
    plugins: {
      ...sharedChartOptions.plugins,
      legend: {
        ...sharedChartOptions.plugins.legend,
        display: true,
      },
    },
    scales: {
      x: {
        ...sharedChartOptions.scales.x,
        stacked: true,
      },
      y: {
        ...sharedChartOptions.scales.y,
        position: "left" as const,
        stacked: true,
        title: {
          display: true,
          text: "Sessions",
          font: CHART_FONT,
        },
        ticks: {
          font: CHART_FONT,
          stepSize: 1,
        },
      },
      y1: {
        grid: { display: false },
        position: "right" as const,
        beginAtZero: true,
        title: {
          display: true,
          text: "kWh",
          font: CHART_FONT,
        },
        ticks: {
          font: CHART_FONT,
          callback: (val: any) => {
            if (val >= 1000) return `${Math.round(val / 1000)}K`;
            return `${val}`;
          },
        },
      },
    },
  };

  return (
    <Card className="shadow-2 h-full" title="Sessions & Energy">
      <Chart type="bar" data={chartData} options={options} style={{ width: "100%" }} />
    </Card>
  );
};

export default SessionsEnergyChart;
